/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Margin, Text } from '@zapier/design-system';
import {
  Currency,
  defaultCurrency,
} from '@zapier/currency/src/hooks/useCurrencies';
import {
  formatPriceToBeInCurrency,
  RoundingStrategy,
} from '@zapier/currency/src/utils/formatPriceToBeInCurrency';

type FancyPriceProps = {
  /** Price in USD pennies (i.e. hundredths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  /**
   * For currencies that don't have decimals/fractional units (e.g. JPY), what type of rounding strategy should we use?
   * For example:
   *   - When showing monthly prices for annual plans, we should round UP in favor of the user (price * 12 will be less than what the user actually pays)
   *   - When showing standard monthly prices, use standard rounding practices i.e. round UP for >= .50 and DOWN for < .50
   */
  roundingStrategy?: RoundingStrategy;
};

/**
 * Returns the decimal separator for the user's locale. Examples:
 *   - Returns . for English
 *   - Returns , for Swedish
 */
const getDecimalSeparator = () => {
  const value = 1.1;
  return value.toLocaleString().substring(1, 2);
};

const root = css`
  display: flex;
  align-items: top;
  margin: 8px;
`;

/**
 * Displays a price in a styled and user-friendly format.
 */
export const FancyPrice = ({
  children,
  roundingStrategy,
  currency = defaultCurrency,
}: FancyPriceProps) => {
  const price = formatPriceToBeInCurrency(children, currency, false, {
    maxDecimals: 2,
    minDecimals: 0,
    roundingStrategy: currency?.zeroDecimal ? roundingStrategy : undefined,
    useRelativeRounding: false,
  });
  const separator = getDecimalSeparator();
  const tokens = price.split(separator);

  return (
    <div css={root}>
      <Margin margin="0 5px 0 0">
        <Text color="gray40" type="sectionHeader1">
          {currency.symbol}
        </Text>
      </Margin>

      <Text color="gray80" type="pageHeader5">
        {tokens[0]}
      </Text>

      {tokens.length > 1 && (
        <Margin margin="5px 0 0 2px">
          <Text color="gray80" type="paragraph3Bold">
            {separator}
            {tokens[1]}
          </Text>
        </Margin>
      )}

      {currency.symbol === '$' && (
        <Margin margin="5px 0 0 5px">
          <Text color="gray40" type="paragraph3Bold">
            {currency.name}
          </Text>
        </Margin>
      )}
    </div>
  );
};
